import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause } from 'lucide-react';
import { parseVideoUrl } from '../lib/video';

interface DirectVideoPlayerProps {
  url: string;
  className?: string;
  autoplay?: boolean;
  muted?: boolean;
}

const DirectVideoPlayer: React.FC<DirectVideoPlayerProps> = ({
  url,
  className = '',
  autoplay = false,
  muted = false,
}) => {
  const info = parseVideoUrl(url);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const onPlay = () => setIsPlaying(true);
    const onPause = () => {
      setIsPlaying(false);
      setShowControls(true);
    };
    const onEnded = () => {
      setIsPlaying(false);
      setShowControls(true);
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);

    if (autoplay) {
      video.play().catch(() => {
        // Autoplay blocked by browser policy
      });
    }

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
    };
  }, [url, autoplay]);

  useEffect(() => {
    if (!isPlaying) return;
    setShowControls(true);
    const timer = setTimeout(() => setShowControls(false), 2000);
    return () => clearTimeout(timer);
  }, [isPlaying]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
    } else {
      video.pause();
    }
  };

  if (!info || info.type !== 'direct') return null;

  return (
    <div className={`relative ${className}`} onClick={togglePlay}>
      <video
        ref={videoRef}
        src={info.url}
        className="w-full h-full object-contain"
        muted={muted}
        playsInline
        webkit-playsinline="true"
        preload="metadata"
        controls={false}
        disablePictureInPicture
        controlsList="nodownload"
      />
      <div
        className={`absolute inset-0 flex items-center justify-center bg-black/20 transition-opacity duration-500 ${
          showControls ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            togglePlay();
          }}
          className="w-16 h-16 rounded-full bg-brand-ink/60 text-brand-paper flex items-center justify-center hover:bg-brand-ink/80 transition-colors"
          aria-label={isPlaying ? '暫停' : '播放'}
        >
          {isPlaying ? (
            <Pause className="w-7 h-7" fill="currentColor" />
          ) : (
            <Play className="w-7 h-7 ml-1" fill="currentColor" />
          )}
        </button>
      </div>
    </div>
  );
};

export default DirectVideoPlayer;
